import React, { useState, useEffect, useMemo } from "react";

import {
  Box,
  Grid,
  Button,
  ButtonGroup,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Fade,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../Components/helpers/Helpers";

// Context
import { useContext } from "react";
import { UserContext } from "../../Components/Context/Context";
import fondoBlanco from "../../assets/fondoblanco.jpg";
import Expedientes from "../../Expedientes/Expedientes";
import api from "../../api/axios";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";

const Ver = () => {
  const navigate = useNavigate();

  const { goPanel, goEdit, goTable, buildEditUrl, buildTableUrl } =
    useLocationHelpers();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("id");
  const [caseControlNumber, setCaseControlNumber] = useState(-1);
  const [flagActiveCases, setFlagActiveCases] = useState(true);

  const [lawyerData, setLawyerData] = useState({
    userEmail: "",
    userName: "",
    userProvince: null, // Cambiado a null para manejar Autocomplete
    userAddress: "",
    userPostalCode: "",
    userNif: "",
    userPhone: "",
  });

  const [companyData, setCompanyData] = useState({});

  const [flag, setFlag] = useState(false);

  const { userData } = useContext(UserContext);

  // Obtener los datos del usuario
  const getDataUsers = async () => {
    setFlag(true);

    const url = `${process.env.REACT_APP_API_URL}/showlawyer`;

    try {
      const response = await api.post(url, { id: userId });
      console.log(response);

      const {
        email,
        name,
        province,
        address,
        postalcode,
        nif,
        phone,
        id,
        company_relation,
        notas,
        rol_relation,
      } = response?.data?.lawyer;

      setCompanyData(company_relation || {});

      setLawyerData({
        userEmail: email,
        userName: name,
        userProvince: province,
        userAddress: address,
        userPostalCode: postalcode,
        userNif: nif,
        userPhone: phone,
        userNotes: notas,
        userType: rol_relation?.type,
      });
    } catch (error) {
      console.error("Error fetching user data:", error); // Mensaje de error más descriptivo
    } finally {
      setFlag(false); // Asegurarse de desactivar la bandera en caso de error o í©xito
    }
  };

  useEffect(() => {
    getDataUsers();
  }, []);

  useEffect(() => {
    console.log(caseControlNumber);
    if (caseControlNumber === 0) {
      setFlagActiveCases(false);
    } else {
      setFlagActiveCases(true);
    }
  }, [caseControlNumber]);

  const labels = {
    userName: "Nombre",
    userEmail: "Correo",
    userProvince: "Provincia",
    userAddress: "Dirección",
    userPostalCode: "Código Postal",
    userNif: "NIF",
    userPhone: "Teléfono",
    userType: "Tipo de Usuario",
  };

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Usuarios" subTitle="Ver" />

      {flag ? (
        <LoadingPage heightProp={"40vh"} isLoading />
      ) : (
        <Fade in={!flag} timeout={500}>
          <Box sx={{ maxWidth: "1100px", margin: "auto", p: 2 }}>
            {/* Card principal */}
            <Card sx={{ borderRadius: "16px", boxShadow: 3 }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  {/* Imagen */}
                  <Grid item xs={12} md={4} textAlign="center">
                    <Avatar
                      src={
                        companyData?.id && companyData?.company_logo
                          ? `${process.env.REACT_APP_BACKEND_HOST}/storage/company/${companyData.id}/${companyData.company_logo}`
                          : fondoBlanco
                      }
                      variant="rounded"
                      sx={{
                        width: 140,
                        height: 140,
                        margin: "auto",
                        borderRadius: "16px",

                        "& img": {
                          objectFit: "contain",
                          padding: "10px",
                        },
                      }}
                    />
                  </Grid>

                  {/* Datos */}
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={1}>
                      {Object.entries(lawyerData).map(([key, value]) =>
                        key !== "userNotes" ? (
                          <Grid item xs={12} sm={6} key={key}>
                            <Typography
                              variant="caption"
                              color="text.secondary">
                              {labels[key]}
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {value || "-"}
                            </Typography>
                          </Grid>
                        ) : null,
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {/* Notas */}
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notas
                  </Typography>

                  <Box
                    sx={{
                      background: "#f8f9fa",
                      borderRadius: "10px",
                      padding: "12px",
                      minHeight: "100px",
                      border: "1px solid rgba(0,0,0,0.1)",
                      whiteSpace: "pre-wrap",
                    }}>
                    {lawyerData?.userNotes || "Sin notas"}
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Botones */}
                <ButtonGroup fullWidth>
                  <NavButton
                    to={buildEditUrl("usuarios", userId)}
                    onClick={() => goEdit("usuarios", userId, "Usuario")}
                    variant="contained"
                    color="success">
                    Editar
                  </NavButton>
                  <NavButton
                    to={buildTableUrl("usuarios")}
                    onClick={() => goTable("usuarios", "Usuarios")}
                    variant="outlined"
                    color="error">
                    Tabla
                  </NavButton>
                </ButtonGroup>
              </CardContent>
            </Card>

            {/* Expedientes */}
            <Box mt={3}>
              <Accordion defaultExpanded sx={{ borderRadius: "12px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="600">Expedientes</Typography>
                </AccordionSummary>

                <AccordionDetails>
                  {flagActiveCases ? (
                    <Expedientes
                      flagToShow={false}
                      userCase={userId}
                      setCaseControlNumber={setCaseControlNumber}
                    />
                  ) : (
                    <Alert severity="info">Sin expedientes asociados...</Alert>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        </Fade>
      )}
    </div>
  );
};

export default Ver;
